import { PdfGen, PdfSize, PdfRect, PdfBrush, PdfUnit, ETextAlign } from '../../pdf/pdf';
import { ILayoutMultiGen } from '../layoutMulti';
import { OptsMulti } from '../optsMulti';
import { GUtil } from '../../pdf/gutil';
import { GCalc } from '../../pdf/gcalc';
import { genMulti } from '../../core/genHelper';
import { urlQr } from '../../core/urlQr';
import { IItemCount, SITE_URL } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/barcode';
import { MM } from '../../modules/ui/controls/primitives';

function genId(rnd: { next(a: number, b: number): number }): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: 4 }, () => chars[rnd.next(0, chars.length)]).join('');
}

/**
 * Multi.QR_Hra__Check
 * Each answer option has a check QR (correct=1/wrong=0)
 * Migrated from C# EduFrame.Multi.Generators.QR_Hra__Check
 */
export class QR_Hra__Check implements ILayoutMultiGen, IItemCount {
  countDefault(): number {
    return 12;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const countY = 4;
    const totalCount = this.countDefault();
    const answerHeight = PdfUnit.FromMillimeters(10);
    const stripH = PdfSize.A4.height / countY;
    const rnd = opts.data.random;
    const id = genId(rnd);

    const gen = genMulti(opts.data, opts.generator, totalCount, true, false);

    for (let i = 0; i < totalCount; i += countY) {
      const p = pdf.addPage();
      const count = Math.min(countY, totalCount - i);

      for (let j = 0; j < count; j++) {
        const q = gen[i + j];
        const rect = new PdfRect(0, j * stripH, PdfSize.A4.width, stripH);
        const r1 = new PdfRect(
          rect.x + margin,
          rect.y + margin,
          rect.width - 2 * margin,
          rect.height * 0.4 - margin
        );
        const r2 = new PdfRect(
          r1.x,
          r1.bottom,
          rect.width - 2 * margin,
          rect.height * 0.6 - margin
        );

        const f = p.adjustFont((r1.width - MM(10)) / q.answers.length, q.answers, GUtil.baseFont);
        p.drawRectangle(GUtil.cutPen, null, rect);
        p.drawStringsMulti(q.texts, GUtil.baseFont, PdfBrush.Black, null, r1, ETextAlign.Center);

        for (let k = 0; k < q.answers.length; k++) {
          const r = new PdfRect(
            r2.x + (k * r2.width) / q.answers.length,
            r2.y,
            r2.width / q.answers.length,
            r2.height
          );
          const rx1 = new PdfRect(r.x, r.y, r.width, answerHeight);
          const rx2 = new PdfRect(r.x, rx1.bottom, r.width, r.height - answerHeight);

          p.drawString(q.answers[k], f, PdfBrush.Black, null, rx1, ETextAlign.Center);

          const rr = GCalc.centerRect(rx2);
          const isCorrect = k === q.answerIndex;
          const nonce = `${rnd.next(0, 10)}${rnd.next(0, 10)}${rnd.next(0, 10)}`;
          const url = urlQr({ a: 'c', id, o: isCorrect ? 1 : 0, rnd: nonce }, SITE_URL);
          p.drawQr(rr, PdfBrush.Black, url);
        }
      }
    }
  }
}

/**
 * Multi.QR_Hra__Quiz
 * Indexed questions with scored QR per answer choice
 * Migrated from C# EduFrame.Multi.Generators.QR_Hra__Quiz
 */
export class QR_Hra__Quiz implements ILayoutMultiGen, IItemCount {
  countDefault(): number {
    return 8;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const countY = 4;
    const totalCount = this.countDefault();
    const answerHeight = PdfUnit.FromMillimeters(10);
    const stripH = PdfSize.A4.height / countY;
    const rnd = opts.data.random;
    const id = genId(rnd);

    const gen = genMulti(opts.data, opts.generator, totalCount, true, false);

    for (let i = 0; i < totalCount; i += countY) {
      const p = pdf.addPage();
      const count = Math.min(countY, totalCount - i);

      for (let j = 0; j < count; j++) {
        const q = gen[i + j];
        const questionIdx = i + j;
        const rect = new PdfRect(0, j * stripH, PdfSize.A4.width, stripH);
        const r1 = new PdfRect(
          rect.x + margin,
          rect.y + margin,
          rect.width - 2 * margin,
          rect.height * 0.4 - margin
        );
        const r2 = new PdfRect(
          r1.x,
          r1.bottom,
          rect.width - 2 * margin,
          rect.height * 0.6 - margin
        );

        const f1 = p.adjustFont(r1.width, q.texts, GUtil.baseFont);
        const f2 = p.adjustFont((r1.width - MM(10)) / q.answers.length, q.answers, GUtil.baseFont);

        p.drawRectangle(GUtil.cutPen, null, rect);
        p.drawStrings(q.texts, f1, PdfBrush.Black, null, r1, ETextAlign.Center);
        p.drawString(
          `#${questionIdx + 1}/${totalCount}`,
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          r1,
          ETextAlign.TopLeft
        );

        for (let k = 0; k < q.answers.length; k++) {
          const r = new PdfRect(
            r2.x + (k * r2.width) / q.answers.length,
            r2.y,
            r2.width / q.answers.length,
            r2.height
          );
          const rx1 = new PdfRect(r.x, r.y, r.width, answerHeight);
          const rx2 = new PdfRect(r.x, rx1.bottom, r.width, r.height - answerHeight);

          p.drawString(q.answers[k], f2, PdfBrush.Black, null, rx1, ETextAlign.Center);

          const rr = GCalc.centerRect(rx2);
          const isCorrect = k === q.answerIndex;
          const url = urlQr(
            { a: 'i', id, c: totalCount, i: questionIdx + 1, v: k + 1, p: isCorrect ? 10 : -3 },
            SITE_URL
          );
          p.drawQr(rr, PdfBrush.Black, url);
        }
      }
    }
  }
}

/**
 * Multi.QR_Hra__Task
 * Multi-answer task with QR encoding answer array
 * Migrated from C# EduFrame.Multi.Generators.QR_Hra__Task
 */
export class QR_Hra__Task implements ILayoutMultiGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const countY = 5;
    const totalCount = this.countDefault();
    const stripH = PdfSize.A4.height / countY;
    const id = genId(opts.data.random);

    const gen = genMulti(opts.data, opts.generator, totalCount, false, false);

    for (let i = 0; i < totalCount; i += countY) {
      const p = pdf.addPage();
      const count = Math.min(countY, totalCount - i);

      for (let j = 0; j < count; j++) {
        const index = i + j + 1;
        const q = gen[i + j];
        const rect = new PdfRect(0, j * stripH, PdfSize.A4.width, stripH);

        const sz = Math.min(rect.width / 4, rect.height);
        const origX = rect.x + (rect.width - 4 * sz) / 2;
        const origY = rect.y + (rect.height - sz) / 2;
        const rs: PdfRect[] = [];
        for (let x = 0; x < 4; x++) {
          rs.push(
            new PdfRect(origX + x * sz + margin, origY + margin, sz - 2 * margin, sz - 2 * margin)
          );
        }
        const rs0 = new PdfRect(rs[1].x, rs[1].y, rs[1].width * 2, rs[1].height);

        const url = urlQr(
          { a: 'g', id, i: index, c: totalCount, t: 'm', o: q.texts.join(' '), m: q.answers },
          SITE_URL
        );

        p.drawString(
          `#${index}/${totalCount}`,
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          rs[0],
          ETextAlign.TopLeft
        );
        p.drawStringsMulti(q.texts, GUtil.baseFont, PdfBrush.Black, null, rs0, ETextAlign.Center);
        p.drawQr(rs[3], PdfBrush.Black, url);
        p.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }
}
